import { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { PageLayout } from '@/components/layout'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { ArrowLeft, RefreshCw, MapPin, Clock, Plus, Trash2 } from 'lucide-react'
import { supabase } from '@/supabase-client'
import { useMarketEventCosts } from '@/hooks/useMarketEventCosts'
import { useSquareSync } from '@/hooks/useSquareSync'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { toast } from 'sonner'

export default function MarketEventDetail({ user, onLogout }) {
  const { eventId } = useParams()
  const navigate = useNavigate()
  const [event, setEvent] = useState(null)
  const [sales, setSales] = useState([])
  const [productMappings, setProductMappings] = useState({})
  const [loading, setLoading] = useState(true)
  const { costs, loading: costsLoading, saveCosts } = useMarketEventCosts(eventId)
  const { syncEvent, syncing } = useSquareSync()

  // Inline cost editing
  const [costRows, setCostRows] = useState([])
  const [costNotes, setCostNotes] = useState('')
  const [savingCosts, setSavingCosts] = useState(false)

  // Sync job info for transaction count
  const [syncInfo, setSyncInfo] = useState(null)

  const fetchEventData = useCallback(async () => {
    setLoading(true)

    const { data: eventData } = await supabase
      .from('market_events')
      .select('*, market_locations(name, address, type)')
      .eq('id', eventId)
      .single()

    setEvent(eventData)

    const { data: salesData } = await supabase
      .from('sales')
      .select(`
        id, date, status, square_order_id,
        sale_items(
          id, quantity, price, sub_total,
          product:products(id, strain),
          product_type:product_types(id, name)
        )
      `)
      .eq('market_event_id', eventId)
      .order('date', { ascending: false })

    setSales(salesData || [])

    // Get product mappings for punnet weight lookup
    const { data: mappings } = await supabase
      .from('square_product_mappings')
      .select('odin_product_id, weight_per_unit_kg')
      .eq('is_active', true)

    const mappingMap = {}
    for (const m of mappings || []) {
      mappingMap[m.odin_product_id] = parseFloat(m.weight_per_unit_kg) || 1
    }
    setProductMappings(mappingMap)

    // Get successful sync job for transaction count + hourly data
    const { data: jobData } = await supabase
      .from('square_sync_jobs')
      .select('orders_synced, sales_created, completed_at, hourly_sales, payout_status, payout_date, payout_fee, payout_paid_at')
      .eq('market_event_id', eventId)
      .eq('status', 'completed')
      .gt('orders_synced', 0)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle()

    setSyncInfo(jobData)
    setLoading(false)
  }, [eventId])

  useEffect(() => {
    fetchEventData()
  }, [fetchEventData])

  // Initialize cost rows from saved costs
  useEffect(() => {
    if (costs) {
      const rows = []
      if (parseFloat(costs.stall_fee) > 0 || !costs.id) rows.push({ category: 'Stall Fee', amount: costs.stall_fee || '' })
      if (parseFloat(costs.staff_cost) > 0 || !costs.id) rows.push({ category: 'Staff Cost', amount: costs.staff_cost || '' })
      if (parseFloat(costs.travel_cost) > 0) rows.push({ category: 'Travel', amount: costs.travel_cost || '' })
      if (parseFloat(costs.other_cost) > 0) rows.push({ category: 'Other', amount: costs.other_cost || '' })
      if (rows.length === 0) rows.push({ category: 'Stall Fee', amount: '' }, { category: 'Staff Cost', amount: '' })
      setCostRows(rows)
      setCostNotes(costs.notes || '')
    } else {
      setCostRows([{ category: 'Stall Fee', amount: '' }, { category: 'Staff Cost', amount: '' }])
      setCostNotes('')
    }
  }, [costs])

  const handleSync = async () => {
    await syncEvent(eventId)
    fetchEventData()
  }

  const handleSaveCosts = async () => {
    setSavingCosts(true)
    try {
      // Map cost rows back to the fixed fields
      const costData = { stall_fee: 0, staff_cost: 0, travel_cost: 0, other_cost: 0, notes: costNotes }
      for (const row of costRows) {
        const amount = parseFloat(row.amount) || 0
        const cat = row.category.toLowerCase()
        if (cat.includes('stall')) costData.stall_fee += amount
        else if (cat.includes('staff')) costData.staff_cost += amount
        else if (cat.includes('travel')) costData.travel_cost += amount
        else costData.other_cost += amount
      }
      await saveCosts(costData)
    } finally {
      setSavingCosts(false)
    }
  }

  const addCostRow = () => setCostRows(prev => [...prev, { category: '', amount: '' }])
  const removeCostRow = (idx) => setCostRows(prev => prev.filter((_, i) => i !== idx))
  const updateCostRow = (idx, field, value) => {
    setCostRows(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row))
  }

  if (loading) {
    return (
      <PageLayout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      </PageLayout>
    )
  }

  if (!event) {
    return (
      <PageLayout user={user} onLogout={onLogout}>
        <p className="text-muted-foreground">Market event not found.</p>
      </PageLayout>
    )
  }

  // Calculate totals
  const allItems = sales.flatMap(sale => sale.sale_items)
  const totalRevenue = allItems.reduce((sum, item) => sum + (parseFloat(item.sub_total) || 0), 0)
  const eventCosts = costRows.reduce((sum, row) => sum + (parseFloat(row.amount) || 0), 0)
  const squareFees = syncInfo?.payout_fee ? parseFloat(syncInfo.payout_fee) : 0
  const totalCosts = eventCosts + squareFees
  const netProfit = totalRevenue - totalCosts
  const transactionCount = syncInfo?.orders_synced || 0
  const avgSale = transactionCount > 0 ? totalRevenue / transactionCount : 0
  const totalKg = allItems.reduce((sum, item) => sum + parseFloat(item.quantity), 0)
  const locationName = event.market_locations?.name || 'Unknown'
  const payoutStatus = syncInfo?.payout_status
  const payoutDate = syncInfo?.payout_date

  // Calculate punnets for each item
  const getPunnets = (item) => {
    const kg = parseFloat(item.quantity)
    const weightPerUnit = productMappings[item.product?.id] || 1
    return Math.round(kg / weightPerUnit)
  }

  // Hourly sales chart data from sync job
  const hourlySales = syncInfo?.hourly_sales || []
  const chartData = hourlySales.map(h => ({
    time: `${String(h.hour).padStart(2, '0')}:00`,
    sales: h.sales,
    orders: h.orders
  }))

  const formatDate = (dateStr) => {
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    })
  }

  const formatTime = (timeStr) => {
    const [h, m] = timeStr.split(':')
    return `${h}:${m}`
  }

  return (
    <PageLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-start justify-between">
          <div>
            <Button
              variant="ghost"
              size="sm"
              className="mb-2 -ml-2"
              onClick={() => navigate('/sales/markets')}
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Markets
            </Button>
            <h1 className="text-3xl font-bold text-foreground">{locationName}</h1>
            <div className="flex items-center gap-4 mt-1 text-muted-foreground">
              <span className="flex items-center gap-1">
                <MapPin className="h-4 w-4" />
                {formatDate(event.date)}
              </span>
              <span className="flex items-center gap-1">
                <Clock className="h-4 w-4" />
                {formatTime(event.start_time)} – {formatTime(event.end_time)}
              </span>
            </div>
          </div>

          <Button
            onClick={handleSync}
            disabled={syncing}
            variant="outline"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync Square'}
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Gross Sales</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">£{totalRevenue.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Transactions</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{transactionCount}</p>
              {avgSale > 0 && <p className="text-xs text-muted-foreground">£{avgSale.toFixed(2)} avg</p>}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Total Weight</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">{totalKg.toFixed(1)}kg</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Costs</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold">£{totalCosts.toFixed(2)}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Net Profit</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                £{netProfit.toFixed(2)}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium text-muted-foreground">Margin</CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-2xl font-bold ${netProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {totalRevenue > 0 ? `${((netProfit / totalRevenue) * 100).toFixed(0)}%` : '—'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Sales vs Time Chart */}
        {chartData.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Sales Through the Day</CardTitle>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={chartData}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="time" />
                  <YAxis tickFormatter={(v) => `£${v}`} />
                  <Tooltip
                    formatter={(value, name) => {
                      if (name === 'sales') return [`£${value.toFixed(2)}`, 'Sales']
                      if (name === 'orders') return [value, 'Orders']
                      return [value, name]
                    }}
                  />
                  <Bar dataKey="sales" fill="#3b82f6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        )}

        {/* Costs Table */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Event Costs</CardTitle>
              <Button variant="ghost" size="sm" onClick={addCostRow}>
                <Plus className="h-4 w-4 mr-1" />
                Add
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="pb-2 font-medium text-muted-foreground">Category</th>
                    <th className="pb-2 font-medium text-muted-foreground text-right w-32">Amount</th>
                    <th className="pb-2 w-10"></th>
                  </tr>
                </thead>
                <tbody>
                  {costRows.map((row, idx) => (
                    <tr key={idx} className="border-b last:border-0">
                      <td className="py-1.5">
                        <Input
                          value={row.category}
                          onChange={(e) => updateCostRow(idx, 'category', e.target.value)}
                          className="h-8 border-0 shadow-none px-0 focus-visible:ring-0"
                          placeholder="Category name..."
                        />
                      </td>
                      <td className="py-1.5">
                        <div className="relative">
                          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground text-sm">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={row.amount}
                            onChange={(e) => updateCostRow(idx, 'amount', e.target.value)}
                            className="h-8 pl-6 text-right border-0 shadow-none focus-visible:ring-0"
                            placeholder="0.00"
                          />
                        </div>
                      </td>
                      <td className="py-1.5 text-right">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => removeCostRow(idx)}>
                          <Trash2 className="h-3.5 w-3.5 text-muted-foreground" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-medium">
                    <td className="py-2">Total</td>
                    <td className="py-2 text-right">£{totalCosts.toFixed(2)}</td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>

              <textarea
                value={costNotes}
                onChange={(e) => setCostNotes(e.target.value)}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring mt-2"
                rows={2}
                placeholder="Notes..."
              />

              <div className="flex justify-end mt-2">
                <Button size="sm" onClick={handleSaveCosts} disabled={savingCosts}>
                  {savingCosts ? 'Saving...' : 'Save Costs'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sales Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Sales</CardTitle>
          </CardHeader>
          <CardContent>
            {sales.length === 0 ? (
              <p className="text-muted-foreground text-sm">
                No sales synced yet. Click "Sync Square" to pull orders.
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left">
                      <th className="pb-2 font-medium text-muted-foreground">Product</th>
                      <th className="pb-2 font-medium text-muted-foreground">Type</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Units</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Qty (kg)</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Price/kg</th>
                      <th className="pb-2 font-medium text-muted-foreground text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allItems.map(item => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="py-2">{item.product?.strain || '—'}</td>
                        <td className="py-2">{item.product_type?.name || '—'}</td>
                        <td className="py-2 text-right">{getPunnets(item)}</td>
                        <td className="py-2 text-right">{parseFloat(item.quantity).toFixed(2)}</td>
                        <td className="py-2 text-right">£{parseFloat(item.price).toFixed(2)}</td>
                        <td className="py-2 text-right font-medium">£{parseFloat(item.sub_total).toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="border-t font-medium">
                      <td colSpan="5" className="py-2 text-right">Total</td>
                      <td className="py-2 text-right">£{totalRevenue.toFixed(2)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </PageLayout>
  )
}
